import { persoonOpDatum } from './koppeling.js';
import { DUBBELE_NAMEN, WOONPLAATSEN } from './woonplaatsen.js';
/**
 * Tanken en laden over de grens: uit de landcode waar die er is, en anders uit de
 * plaatsnaam.
 *
 * ### Twee bronnen, en waarom de plaatsnaam de zwakste is
 *
 * De factuur heeft een kolom `Land`, maar hij staat maar op een deel van de regels.
 * **DKV Euro Service** (de Europese pasmaatschappij) en **declaraties** vullen hem
 * met een nationaliteitsletter: `F`, `D`, `A`, `I`, `B`, `DK`, `S`. Een gewone
 * Nederlandse pomp laat hem leeg. En een **laadregel heeft hem nooit** — ook niet
 * bij een laadsessie in Frankrijk. Daar blijft de plaats het enige dat verschilt:
 * de btw is 21% ongeacht waar geladen is want MoveMove factureert Nederlands, en
 * het station heet overal `NL-MTE laadtoken EU`.
 *
 * Waar de landcode er wél is, gaat hij vóór. Dat is geen voorkeur maar noodzaak:
 * bij DKV zet het transactieoverzicht de **vestigingsplaats van DKV Nederland** in
 * de locatiekolom in plaats van waar getankt is. 32 regels staan zo op
 * `NOORDWIJKERHOUT` en de declaraties op `HOOFDDORP`, allebei bestaande
 * Nederlandse woonplaatsen. De plaatsnaam is daar dus niet zwak bewijs maar
 * misleidend bewijs, en hij zou met overtuiging `nederland` zeggen over een
 * tankbeurt in Frankrijk. Zo bleven 38 regels van € 2.438,73 onzichtbaar naast de
 * acht laadsessies van € 194,19 die wél gevonden werden.
 *
 * Let op de valstrik in de tegenovergestelde richting: `land` bevatte tot
 * 07-08-2026 op 1.247 laadregels `EU`, en dat was geen land maar een stuk van de
 * stationsnaam dat de oude tekstsplitsing meepikte. Zie `extractPdfLines`.
 *
 * ### Drie uitkomsten, want twee zou liegen
 *
 * - **`buitenland`** — er staat geen enkele Nederlandse woonplaats in het veld.
 *   Assevillers, Sommesous, Venarey-les-Laumes.
 * - **`twijfel`** — er staat wél een Nederlandse woonplaats, maar die naam bestaat
 *   identiek gespeld ook in België, Luxemburg, Duitsland of Frankrijk. Hasselt,
 *   Bergen, Beek, Essen.
 * - **`nederland`** — de rest.
 *
 * Van de Nederlandse woonplaatsen bestaan er 498 precies zo ook over de grens. Op de
 * hele database levert dat 8 zekere gevallen op en 21 twijfelregels, verdeeld over
 * vijf plaatsen: Hoogland, Ede, Rutten, Beek LB en Dorst.
 *
 * Vergelijken gebeurt mét accenten, en dat is geen detail: zonder dat wordt het
 * Franse `Méteren` het Gelderse `Meteren` en het Duitse `Lünen` het Gelderse `Lunen`.
 * Zie `normPlaats`.
 *
 * ### Waarom er niet naar de rit eromheen gekeken wordt
 *
 * Dat heeft er wel in gezeten: een dubbele naam telde pas als er een zekere
 * buitenlandregel dichtbij stond, of als er drie tankbeurten op één dag waren. Het
 * idee klopt — laad je 's ochtends in Couvin en 's middags in Sommesous, dan was je
 * daartussen niet in Limburg — maar als bewijs deugt het niet:
 *
 * - **Een volle tank vóór de grens** maakt een buitenlandrit onzichtbaar. Er is dan
 *   geen enkele transactie die de rit verraadt.
 * - **Andersom vuurde hij op onschuldige dagen.** P552BP parkeerde 15:12 in
 *   Hoofddorp, tankte 17:04 in Groningen en parkeerde 19:28 weer in Hoofddorp; dat
 *   telde als lange rit, terwijl dat parkeren juist bewijst dat de auto hier was.
 *
 * Een aanwijzing die in beide richtingen faalt is geen aanwijzing, en hij kostte
 * drie afstelknoppen. Een korte twijfellijst die een mens zelf nakijkt is eerlijker.
 * Zie `TransactieOordeel` voor waar dat antwoord landt, en waarom het niet vooruit
 * werkt.
 */
const NEDERLANDS = new Set(WOONPLAATSEN);
const DUBBEL = new Set(DUBBELE_NAMEN);
/**
 * Landcodes zoals MoveMove ze in de kolom `Land` schrijft: de nationaliteits
 * letter, niet ISO. Dus `D` en niet `DE`, `F` en niet `FR`.
 *
 * De lijst is bewust kort en niet compleet. Elke code die ook een Nederlands
 * provincie-achtervoegsel is (`ZH`, `LB`, `NB`, `FR`, `GR`) of het eerste woord van
 * een plaatsnaam (`DE` in DE MEERN, DE BILT) zou hier valse buitenlandmeldingen
 * opleveren, en dat is duurder dan een gemiste: een gemiste komt in de
 * twijfellijst terecht, een valse boekt een binnenlandse tankbeurt als buitenland
 * zonder dat iemand ernaar kijkt. Komt er een nieuw land langs, dan valt dat op als
 * een DKV-regel zonder land.
 *
 * `NL` staat er niet in maar wordt wel afgehandeld: zie `oordeelUitLand`.
 */
const LANDEN = new Map([
    ['D', 'Duitsland'],
    ['F', 'Frankrijk'],
    ['B', 'België'],
    ['L', 'Luxemburg'],
    ['A', 'Oostenrijk'],
    ['CH', 'Zwitserland'],
    ['I', 'Italië'],
    ['E', 'Spanje'],
    ['P', 'Portugal'],
    ['DK', 'Denemarken'],
    ['S', 'Zweden'],
    ['N', 'Noorwegen'],
    ['PL', 'Polen'],
    ['CZ', 'Tsjechië'],
    ['SK', 'Slowakije'],
    ['H', 'Hongarije'],
    ['HR', 'Kroatië'],
    ['SLO', 'Slovenië'],
]);
export function isLandcode(waarde) {
    return LANDEN.has(waarde.trim().toUpperCase());
}
/** De landnaam bij een code, of de code zelf als we hem niet kennen. */
export function landNaam(code) {
    const c = code.trim().toUpperCase();
    return LANDEN.get(c) ?? c;
}
/**
 * Het oordeel dat rechtstreeks uit de landkolom volgt, of `null` als die kolom
 * niets zegt.
 *
 * Dit is de enige harde bron die de factuur over het land geeft, en hij staat maar
 * op een deel van de regels: DKV Euro Service (de Europese pasmaatschappij) en
 * declaraties vullen hem, een gewone Nederlandse pomp laat hem leeg, en een
 * laadregel heeft hem nooit — ook niet bij een laadsessie in Frankrijk. Waar hij
 * er niet is blijft de plaatsnaam het enige dat er is.
 */
export function oordeelUitLand(land) {
    if (!land)
        return null;
    const code = land.trim().toUpperCase();
    if (code === 'NL')
        return 'nederland';
    return isLandcode(code) ? 'buitenland' : null;
}
/**
 * Naar de vorm waarin we vergelijken: hoofdletters, leestekens worden spaties, en
 * **accenten blijven staan**.
 *
 * Dat laatste is het hele punt. De factuur schrijft accenten gewoon uit — er staat
 * letterlijk `SAINT-VIÂTRE` in onze data. Zou je ze wegstrepen, dan wordt het Franse
 * `Méteren` het Gelderse `Meteren` en het Duitse `Lünen` (86.000 inwoners) het
 * Gelderse `Lunen`, allebei stilletjes "Nederland". Zo'n gemiste melding is duurder
 * dan een valse: een valse zie je staan, deze niet.
 *
 * Van de Nederlandse woonplaatsen staat de accentloze vorm óók in de lijst, dus een
 * leverancier die `Skarsterlan` schrijft geeft geen vals alarm.
 *
 * De lijsten in `woonplaatsen.ts` staan al in deze vorm. Verander je deze bewerking,
 * verander dan die lijsten mee — wijken ze uiteen, dan matcht er niets meer en heet
 * heel Nederland ineens buitenland.
 */
export function normPlaats(s) {
    return s
        .toUpperCase()
        .replace(/[^\p{L}\p{N}]+/gu, ' ')
        .trim();
}
/**
 * De Nederlandse woonplaats die in dit veld staat, of null.
 *
 * Zoekt elke aaneengesloten reeks woorden af, en niet alleen de hele tekst. Dat is
 * nodig omdat de stationsnaam meelift: `VOF NOORDWIJKERHOUT D`, `BR UTRECHT`,
 * `TOTAL WOERDEN INZ HOLLANDBAAN WOERDEN`, `001-GOUD GOUDA`. De langste treffer
 * wint, zodat `ALPHEN AAN DEN RIJN` niet op `ALPHEN` blijft steken.
 */
export function nederlandsePlaatsIn(plaats) {
    const woorden = normPlaats(plaats).split(' ').filter(Boolean);
    let beste = null;
    for (let i = 0; i < woorden.length; i++) {
        for (let j = woorden.length; j > i; j--) {
            const kandidaat = woorden.slice(i, j).join(' ');
            if (NEDERLANDS.has(kandidaat) && (beste === null || kandidaat.length > beste.length)) {
                beste = kandidaat;
            }
        }
    }
    return beste;
}
/** Het oordeel over één plaatsnaam, vóór een eventueel `TransactieOordeel`. */
export function beoordeelPlaats(plaats) {
    const treffer = nederlandsePlaatsIn(plaats);
    if (treffer === null)
        return 'buitenland';
    return DUBBEL.has(treffer) ? 'twijfel' : 'nederland';
}
/**
 * Wat vaststaat per maand, en waarover nog beslist moet worden.
 *
 * Twee uitgangen, want het zijn twee vragen aan twee plekken op het scherm. Wat
 * vaststaat is een overzicht — wie tankte waar, en draai er een uitdraai van. Wat
 * twijfelt is werk: een vraag per tankbeurt, gebundeld per plaats zodat het te doen
 * blijft, en die hoort in het actiecentrum bij de andere dingen die een antwoord
 * vragen.
 *
 * Transacties zonder plaats of zonder datum blijven overal buiten en worden in
 * `dekking` geteld: daar is geen oordeel over te geven.
 */
export function bouwBuitenlandOverzicht(lines, opts = {}) {
    const koppelingen = opts.koppelingen ?? [];
    const persoonNaam = new Map((opts.personen ?? []).map((p) => [p.id, p.naam]));
    const pasnummer = new Map((opts.passen ?? []).map((p) => [p.id, p.pasnummer]));
    const besloten = new Map((opts.oordelen ?? []).map((o) => [o.costLineId, o.oordeel]));
    const maandFilter = opts.maanden ? new Set(opts.maanden) : null;
    const perTwijfel = new Map();
    // Elke maand waarover dit overzicht gaat krijgt een plek, ook als er niets in komt
    // te staan: de maandkiezer moet hem kunnen aanbieden, en de dekking van een maand
    // zonder buitenlandtransacties is nog steeds een antwoord.
    const perMaand = new Map();
    const maandVan = (maand) => {
        const m = perMaand.get(maand) ?? {
            maand,
            transacties: [],
            bedragExcl: 0,
            dekking: { metPlaats: 0, zonderPlaats: 0 },
        };
        perMaand.set(maand, m);
        return m;
    };
    for (const m of opts.maanden ?? [])
        maandVan(m);
    for (const l of lines) {
        if (!l.maand)
            continue;
        if (maandFilter && !maandFilter.has(l.maand))
            continue;
        const maandRij = maandVan(l.maand);
        // De landcode uit de factuur wordt vóór de plaatscheck bekeken, want hij is de
        // enige harde bron en hij heeft de plaats niet nodig.
        const uitLand = oordeelUitLand(l.land);
        if ((!l.plaats?.trim() && !uitLand) || !l.datum) {
            maandRij.dekking.zonderPlaats++;
            continue;
        }
        if (l.plaats?.trim())
            maandRij.dekking.metPlaats++;
        /**
         * Drie bronnen, in deze rangorde, en die orde is het hele punt.
         *
         * 1. **Het besluit van de fleetmanager.** Hij heeft déze tankbeurt nagekeken.
         *    Opgezocht op de regel en niet op de plaats, zodat het antwoord blijft waar
         *    het over ging.
         * 2. **De landcode.** Een feit uit de factuur, geen gok.
         * 3. **De plaatsnaam.** Alleen als de eerste twee zwijgen.
         *
         * Dat de landcode vóór de plaatsnaam gaat is niet netjesheid maar noodzaak. Bij
         * DKV zet het transactieoverzicht de **vestigingsplaats van DKV Nederland** in
         * de locatiekolom in plaats van waar getankt is: 32 regels staan op
         * `NOORDWIJKERHOUT` en de declaraties op `HOOFDDORP`, allebei bestaande
         * Nederlandse woonplaatsen. De plaatsnaam is daar dus niet zwak bewijs maar
         * misleidend bewijs — hij zou met overtuiging `nederland` zeggen over een
         * tankbeurt in Frankrijk. Zo bleven 38 regels van € 2.438,73 onzichtbaar,
         * tegen de acht laadsessies van € 194,19 die wél gevonden werden.
         */
        const besluit = besloten.get(l.id);
        const oordeel = besluit
            ? besluit === 'NEDERLAND'
                ? 'nederland'
                : 'buitenland'
            : (uitLand ?? beoordeelPlaats(l.plaats));
        if (oordeel === 'nederland')
            continue;
        // Leeg en niet null: een regel die alleen op zijn landcode als buitenland
        // aangemerkt is heeft geen plaats om te tonen, en het scherm valt dan terug op
        // het land.
        const plaatsRuw = l.plaats?.trim() ?? '';
        const plaats = normPlaats(plaatsRuw);
        const persoonId = l.cardId ? persoonOpDatum(koppelingen, l.cardId, l.datum) : null;
        const naam = persoonId ? (persoonNaam.get(persoonId) ?? null) : null;
        const bedrag = Number(l.bedragExcl) || 0;
        if (oordeel === 'twijfel') {
            const t = perTwijfel.get(plaats) ?? {
                plaats,
                plaatsRuw,
                bedragExcl: 0,
                transacties: [],
            };
            t.bedragExcl += bedrag;
            t.transacties.push({
                id: l.id,
                datum: l.datum,
                tijd: l.tijd,
                kenteken: l.kenteken,
                persoonId,
                persoonNaam: naam,
                bedragExcl: bedrag,
            });
            perTwijfel.set(plaats, t);
            continue;
        }
        maandRij.transacties.push({
            id: l.id,
            maand: l.maand,
            datum: l.datum,
            tijd: l.tijd,
            kenteken: l.kenteken,
            persoonId,
            persoonNaam: naam,
            pasnummer: l.cardId ? (pasnummer.get(l.cardId) ?? null) : null,
            plaatsRuw,
            plaats,
            // Alleen een erkende landcode, en niet wat er verder in de kolom staat: dit
            // veld betekent "de code die deze regel hier bracht". Een onbekende waarde
            // zou anders als land op het scherm komen — met de oude `EU` in de database
            // stond er letterlijk "EU · BILZEN" bij een laadsessie waar de plaatsnaam het
            // werk deed.
            land: uitLand === 'buitenland' ? (l.land?.trim().toUpperCase() ?? null) : null,
            station: l.station,
            categorie: l.categorie,
            hoeveelheid: l.hoeveelheid,
            eenheid: l.eenheid,
            prijs: l.prijs,
            bedragExcl: bedrag,
            bedragBtw: Number(l.bedragBtw) || 0,
            bevestigd: besluit === 'BUITENLAND',
        });
        maandRij.bedragExcl += bedrag;
    }
    // Nieuwste maand eerst; daarbinnen op datum en tijd, zodat een rit in de volgorde
    // staat waarin hij gereden is.
    const maanden = [...perMaand.values()].sort((a, b) => b.maand.localeCompare(a.maand));
    for (const m of maanden) {
        m.transacties.sort((a, b) => a.datum.localeCompare(b.datum) ||
            (a.tijd ?? '').localeCompare(b.tijd ?? '') ||
            a.plaats.localeCompare(b.plaats));
    }
    // De bundel die het meest gekost heeft bovenaan: daar loont nakijken het meest.
    // Binnen een bundel op datum, want dan lees je een reis en niet een lijst.
    const twijfel = [...perTwijfel.values()].sort((a, b) => b.bedragExcl - a.bedragExcl || a.plaats.localeCompare(b.plaats));
    for (const t of twijfel) {
        t.transacties.sort((a, b) => a.datum.localeCompare(b.datum) || (a.tijd ?? '').localeCompare(b.tijd ?? ''));
    }
    return { maanden, twijfel };
}
/**
 * De maanden die onder een periodekeuze vallen, oudste eerst.
 *
 * `ALL` is elke maand waar data van is, `YTD` het nieuwste **datajaar** en niet het
 * kalenderjaar van vandaag: de MTC-factuur komt met een maand vertraging, dus in
 * januari zou "dit jaar" anders een leeg overzicht opleveren terwijl december net
 * binnen is. `YYYY-MM` is die ene maand.
 *
 * `buildDashboard` lost zijn eigen periodechip los hiervan op, want die moet ook een
 * onbekend teken opvangen (dan valt hij terug op de nieuwste maand) en er een label
 * bij maken. Hier hoort dat niet: wie een default wil, kiest die vóór het aanroepen.
 */
export function maandenInPeriode(maanden, periode) {
    const oplopend = [...maanden].sort();
    if (periode === 'ALL')
        return oplopend;
    if (periode === 'YTD') {
        const jaar = oplopend[oplopend.length - 1]?.slice(0, 4);
        return jaar ? oplopend.filter((m) => m.startsWith(`${jaar}-`)) : [];
    }
    return oplopend.filter((m) => m === periode);
}
/**
 * Wat er in een periode over de grens getankt is: alles, dit jaar, of één maand.
 *
 * Bestaat omdat het paneel en de Excel-uitdraai dezelfde vraag stellen en hetzelfde
 * antwoord moeten geven. Zou de uitdraai zijn eigen optelling doen, dan kan hij van
 * het scherm gaan afwijken zonder dat iemand het merkt — en die uitdraai gaat naar
 * de administratie.
 *
 * De transacties komen chronologisch uit: `maandenInPeriode` levert de maanden
 * oplopend en `bouwBuitenlandOverzicht` sorteert binnen een maand al op datum en
 * tijd, dus achter elkaar plakken is genoeg.
 */
export function buitenlandInPeriode(maanden, periode) {
    const perMaand = new Map(maanden.map((m) => [m.maand, m]));
    const scope = maandenInPeriode([...perMaand.keys()], periode);
    const rijen = scope.map((m) => perMaand.get(m));
    return {
        periode,
        maanden: scope,
        transacties: rijen.flatMap((m) => m.transacties),
        // Op centen afgerond: dertien maandtotalen bij elkaar levert anders een
        // vloeikommastaart op, en dan wijkt het schermtotaal een cent van de uitdraai af.
        bedragExcl: Math.round(rijen.reduce((s, m) => s + m.bedragExcl, 0) * 100) / 100,
        dekking: {
            metPlaats: rijen.reduce((s, m) => s + m.dekking.metPlaats, 0),
            zonderPlaats: rijen.reduce((s, m) => s + m.dekking.zonderPlaats, 0),
        },
    };
}
//# sourceMappingURL=buitenland.js.map